import React, { useState, useMemo } from 'react';
import {
  AlertTriangle, Copy, Zap, RefreshCw, X, UserX, CheckCircle2,
  History, Undo2, ClipboardCheck, Filter,
} from 'lucide-react';
import {
  useDetecterExamensNonFaitsQuery,
  useAnalyserCompletudeExamenMutation,
  useAttribuerZeroAbsentsMutation,
  useNoteCollectiveExamenMutation,
  useNoteSeuilExamenMutation,
  useAnnulerBatchExamenMutation,
  useGetBatchesRecentsExamensQuery,
  ExamenAlertEntry,
  EtudiantManquantExamen,
} from '../../services/notesExamensApi';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetClassesQuery, useGetParcoursListQuery } from '../../services/polesOptionsApi';
import { statistiquesService } from '../../services/api.service';

interface Props {
  anneeAcademique: string;
}

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];
const MOTIFS = [
  { v: 'ABSENCE_JUSTIFIEE', l: 'Absence (justifiée ou non)' },
  { v: 'DECISION_PEDAGOGIQUE', l: 'Décision pédagogique' },
  { v: 'ERREUR_SAISIE', l: 'Correction erreur de saisie' },
  { v: 'AUTRE', l: 'Autre (voir commentaire)' },
];

const ActionsRapidesExamens: React.FC<Props> = ({ anneeAcademique }) => {
  const [semestre, setSemestre] = useState('S1');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtrePole, setFiltrePole] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Exiger un filtre (niveau OU pôle) — sinon scan de toutes matières × classes (lourd)
  const hasFilter = !!(filtreNiveau || filtrePole);

  // Queries
  const { data: detection, isLoading: detecting, refetch: refetchDetection } = useDetecterExamensNonFaitsQuery(
    { annee_academique: anneeAcademique, semestre, niveau: filtreNiveau || undefined, pole: filtrePole || undefined },
    { skip: !anneeAcademique || !hasFilter }
  );

  const MAX_ROWS_DISPLAY = 200;
  const { data: matieres = [] } = useGetMatieresQuery();
  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const { data: batchesData, refetch: refetchBatches } = useGetBatchesRecentsExamensQuery({ limit: 15 });

  // Mutations
  const [analyser] = useAnalyserCompletudeExamenMutation();
  const [attribuerZero, { isLoading: applyingZero }] = useAttribuerZeroAbsentsMutation();
  const [noteCollective, { isLoading: applyingColl }] = useNoteCollectiveExamenMutation();
  const [noteSeuil, { isLoading: applyingSeuil }] = useNoteSeuilExamenMutation();
  const [annulerBatch, { isLoading: annulatingBatch }] = useAnnulerBatchExamenMutation();

  // État modal note collective
  const [showColl, setShowColl] = useState(false);
  const [collForm, setCollForm] = useState({
    matiere_id: '', classes: [] as string[],
    valeur: '', ecraser: false,
    motif: 'DECISION_PEDAGOGIQUE', commentaire: '',
    scope: 'classes' as 'classes' | 'niveau' | 'parcours' | 'matiere',
    scopeNiveau: '',
    scopeParcours: '',
  });
  const [collPreview, setCollPreview] = useState<null | { nb_classes: number; nb_etudiants: number; classes: string[] }>(null);
  const { data: parcoursList = [] } = useGetParcoursListQuery({ actif: true });

  // État modal "note <= seuil → valeur"
  const [showSeuil, setShowSeuil] = useState(false);
  const [seuilForm, setSeuilForm] = useState({
    matiere_id: '', classes: [] as string[],
    seuil: '', valeur: '', inclureAbsents: false,
    motif: 'DECISION_PEDAGOGIQUE', commentaire: '',
    scope: 'classes' as 'classes' | 'niveau' | 'parcours' | 'matiere',
    scopeNiveau: '', scopeParcours: '',
  });
  const [seuilMatiereSearch, setSeuilMatiereSearch] = useState('');
  const [seuilPreview, setSeuilPreview] = useState<null | { nb_classes: number; nb_etudiants: number; classes: string[] }>(null);
  const seuilMatieresFiltrees = useMemo(() => {
    if (!seuilMatiereSearch || seuilMatiereSearch.length < 2) return [];
    const q = seuilMatiereSearch.toLowerCase();
    return (matieres as any[]).filter((m: any) =>
      m.nom_matiere?.toLowerCase().includes(q) || m.code_matiere?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [matieres, seuilMatiereSearch]);

  // État modal zéro absents (analyse + sélection)
  const [zeroCtx, setZeroCtx] = useState<null | { matiereId: number; matiereNom: string; classe: string }>(null);
  const [zeroAnalyse, setZeroAnalyse] = useState<null | {
    absents: EtudiantManquantExamen[]; manquants: EtudiantManquantExamen[];
    stats: { total: number; pct_avec_note: number; pct_absents: number; pct_manquants: number; };
  }>(null);
  const [zeroSelection, setZeroSelection] = useState<Set<number>>(new Set());
  const [zeroForm, setZeroForm] = useState({ motif: 'ABSENCE_JUSTIFIEE', commentaire: '' });

  // Poles & matières filtrées
  const poles = useMemo(() => {
    const s = new Set((classes as any[]).map((c: any) => c.pole_nom).filter(Boolean));
    return Array.from(s).sort();
  }, [classes]);

  const [matiereSearch, setMatiereSearch] = useState('');
  const matieresFiltrees = useMemo(() => {
    if (!matiereSearch || matiereSearch.length < 2) return [];
    const q = matiereSearch.toLowerCase();
    return (matieres as any[]).filter((m: any) =>
      m.nom_matiere?.toLowerCase().includes(q) || m.code_matiere?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [matieres, matiereSearch]);

  // Ouvre le modal "zéro absents" pour une matière+classe donnée
  const handleOuvrirZero = async (m: ExamenAlertEntry) => {
    setZeroCtx({ matiereId: m.matiere_id, matiereNom: m.matiere_nom, classe: m.classe });
    setZeroAnalyse(null);
    setZeroSelection(new Set());
    try {
      const res = await analyser({
        matiere_id: m.matiere_id,
        annee_academique: anneeAcademique,
        semestre,
        classes: [m.classe],
      }).unwrap();
      setZeroAnalyse({
        absents: res.absents,
        manquants: res.manquants,
        stats: {
          total: res.total_etudiants,
          pct_avec_note: res.pct_avec_note,
          pct_absents: res.pct_absents,
          pct_manquants: res.pct_manquants,
        },
      });
      // Présélection de tous les absents + manquants par défaut
      const all = new Set<number>();
      res.absents.forEach(e => all.add(e.etudiant_id));
      res.manquants.forEach(e => all.add(e.etudiant_id));
      setZeroSelection(all);
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || 'Erreur d\'analyse' });
      setZeroCtx(null);
    }
  };

  const handleAppliquerZero = async () => {
    if (!zeroCtx || zeroSelection.size === 0) return;
    try {
      const res = await attribuerZero({
        matiere_id: zeroCtx.matiereId,
        etudiants_ids: Array.from(zeroSelection),
        annee_academique: anneeAcademique,
        semestre,
        motif: zeroForm.motif,
        commentaire: zeroForm.commentaire,
      }).unwrap();
      setMsg({
        type: 'success',
        text: `${res.nb_total_traites} étudiant(s) traité(s) — ${res.nb_crees} créé(s), ${res.nb_modifies} modifié(s), ${res.nb_ignores} ignoré(s)`,
      });
      setZeroCtx(null);
      refetchDetection();
      refetchBatches();
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  // Prévisualise combien de classes/étudiants seront concernés par le scope
  const handlePreviewScope = async () => {
    if (!collForm.matiere_id) {
      setCollPreview(null);
      return;
    }
    try {
      const res = await statistiquesService.previewScopeNotes({
        scope: collForm.scope,
        matiere_id: Number(collForm.matiere_id),
        classes: collForm.classes,
        niveau: collForm.scopeNiveau || undefined,
        parcours_nom: collForm.scopeParcours || undefined,
      });
      setCollPreview({ nb_classes: res.nb_classes, nb_etudiants: res.nb_etudiants, classes: res.classes });
    } catch {
      setCollPreview(null);
    }
  };

  const handleAppliquerCollective = async () => {
    if (!collForm.matiere_id || !collForm.valeur) {
      setMsg({ type: 'error', text: 'Remplissez la matière et la valeur' });
      return;
    }
    if (collForm.scope === 'classes' && collForm.classes.length === 0) {
      setMsg({ type: 'error', text: 'Sélectionnez au moins une classe' });
      return;
    }
    if (collForm.scope === 'parcours' && !collForm.scopeParcours) {
      setMsg({ type: 'error', text: 'Choisissez un parcours' });
      return;
    }
    try {
      const payload: any = {
        matiere_id: Number(collForm.matiere_id),
        annee_academique: anneeAcademique,
        semestre,
        valeur: Number(collForm.valeur),
        ecraser_existants: collForm.ecraser,
        motif: collForm.motif,
        commentaire: collForm.commentaire,
        scope: collForm.scope,
      };
      if (collForm.scope === 'classes') payload.classes = collForm.classes;
      if (collForm.scope === 'niveau') payload.niveau = collForm.scopeNiveau;
      if (collForm.scope === 'parcours') payload.parcours_nom = collForm.scopeParcours;
      const res = await noteCollective(payload).unwrap();
      setMsg({
        type: 'success',
        text: `Note ${res.valeur}/20 attribuée : ${res.nb_total_traites} traité(s) (${res.nb_crees} créé(s), ${res.nb_modifies} modifié(s), ${res.nb_ignores} ignoré(s))`,
      });
      setShowColl(false);
      setCollPreview(null);
      refetchDetection();
      refetchBatches();
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  // Prévisualise le périmètre (nb étudiants total) pour le modal seuil
  const handlePreviewSeuil = async () => {
    if (!seuilForm.matiere_id) { setSeuilPreview(null); return; }
    try {
      const res = await statistiquesService.previewScopeNotes({
        scope: seuilForm.scope,
        matiere_id: Number(seuilForm.matiere_id),
        classes: seuilForm.classes,
        niveau: seuilForm.scopeNiveau || undefined,
        parcours_nom: seuilForm.scopeParcours || undefined,
      });
      setSeuilPreview({ nb_classes: res.nb_classes, nb_etudiants: res.nb_etudiants, classes: res.classes });
    } catch {
      setSeuilPreview(null);
    }
  };

  const handleAppliquerSeuil = async () => {
    if (!seuilForm.matiere_id || seuilForm.seuil === '' || seuilForm.valeur === '') {
      setMsg({ type: 'error', text: 'Renseignez la matière, le seuil et la note à attribuer' });
      return;
    }
    if (seuilForm.scope === 'classes' && seuilForm.classes.length === 0) {
      setMsg({ type: 'error', text: 'Sélectionnez au moins une classe' });
      return;
    }
    if (seuilForm.scope === 'niveau' && !seuilForm.scopeNiveau) {
      setMsg({ type: 'error', text: 'Choisissez un niveau' });
      return;
    }
    if (seuilForm.scope === 'parcours' && !seuilForm.scopeParcours) {
      setMsg({ type: 'error', text: 'Choisissez un parcours' });
      return;
    }
    try {
      const payload: any = {
        matiere_id: Number(seuilForm.matiere_id),
        annee_academique: anneeAcademique,
        semestre,
        seuil: Number(seuilForm.seuil),
        valeur: Number(seuilForm.valeur),
        inclure_absents: seuilForm.inclureAbsents,
        motif: seuilForm.motif,
        commentaire: seuilForm.commentaire,
        scope: seuilForm.scope,
      };
      if (seuilForm.scope === 'classes') payload.classes = seuilForm.classes;
      if (seuilForm.scope === 'niveau') payload.niveau = seuilForm.scopeNiveau;
      if (seuilForm.scope === 'parcours') payload.parcours_nom = seuilForm.scopeParcours;
      const res = await noteSeuil(payload).unwrap();
      setMsg({
        type: 'success',
        text: `Note ${res.valeur}/20 attribuée aux étudiants ayant une note ≤ ${res.seuil} : ${res.nb_modifies} modifié(s), ${res.nb_inchangees} déjà à cette valeur`,
      });
      setShowSeuil(false);
      setSeuilPreview(null);
      refetchDetection();
      refetchBatches();
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  const toggleClasseSeuil = (cls: string) => {
    setSeuilForm(f => ({
      ...f,
      classes: f.classes.includes(cls) ? f.classes.filter(c => c !== cls) : [...f.classes, cls],
    }));
  };

  const handleAnnuler = async (batchId: string) => {
    if (!window.confirm('Annuler toutes les modifications de ce batch ? Les notes seront restaurées à leur valeur précédente.')) return;
    try {
      const res = await annulerBatch({ batch_id: batchId }).unwrap();
      setMsg({ type: 'success', text: `${res.nb_modifications_restaurees} modification(s) annulée(s)` });
      refetchDetection();
      refetchBatches();
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  const toggleClasseColl = (cls: string) => {
    setCollForm(f => ({
      ...f,
      classes: f.classes.includes(cls) ? f.classes.filter(c => c !== cls) : [...f.classes, cls],
    }));
  };

  const toggleSelectionZero = (id: number) => {
    setZeroSelection(s => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const labelType = (t: string): string => ({
    note_collective_examen: 'Note collective',
    note_seuil_examen: 'Note ≤ seuil',
    zero_absents_examen: 'Zéro absents',
    annulation_batch: 'Annulation',
  }[t] || t);

  return (
    <div className="space-y-6">
      {msg && (
        <div className={`p-3 rounded-lg text-sm flex justify-between items-start ${
          msg.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)}><X size={14} /></button>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
            <select value={semestre} onChange={e => setSemestre(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="S1">S1</option>
              <option value="S2">S2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
            <select value={filtreNiveau} onChange={e => setFiltreNiveau(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="">Tous</option>
              {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle</label>
            <select value={filtrePole} onChange={e => setFiltrePole(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="">Tous</option>
              {poles.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button onClick={() => refetchDetection()} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
            <RefreshCw size={16} className={detecting ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowSeuil(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">
            <Filter size={14} /> Note ≤ seuil
          </button>
          <button onClick={() => setShowColl(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">
            <Copy size={14} /> Note collective
          </button>
        </div>
      </div>

      {/* Message si aucun filtre choisi */}
      {!hasFilter && (
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-200">
          <strong>Choisissez au moins un niveau ou un pôle</strong> ci-dessus pour lancer la détection.
          <br />
          <span className="text-xs opacity-80">
            Sans filtre, l'analyse scanne toutes les matières × classes (plusieurs milliers de combinaisons) et peut ralentir fortement le navigateur.
          </span>
        </div>
      )}

      {/* Stats détection */}
      {detection && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-red-50 dark:bg-red-900/15 border-2 border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-600 text-white rounded-lg"><AlertTriangle size={20} /></div>
              <div>
                <div className="text-2xl font-bold text-red-800 dark:text-red-200">{detection.total_non_faits}</div>
                <div className="text-xs text-red-600 dark:text-red-400">Examens sans aucune note</div>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/15 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-600 text-white rounded-lg"><Zap size={20} /></div>
              <div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">{detection.total_partiels}</div>
                <div className="text-xs text-amber-600 dark:text-amber-400">Saisie partielle (&lt;80%)</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tableau : examens non faits + partiels */}
      {detection && (detection.total_non_faits + detection.total_partiels > 0) ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-medium text-sm">
            Matières × Classes problématiques
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">Matière</th>
                  <th className="px-3 py-2 text-left">Classe</th>
                  <th className="px-3 py-2 text-center">Étudiants</th>
                  <th className="px-3 py-2 text-center">Avec note</th>
                  <th className="px-3 py-2 text-center">Absents</th>
                  <th className="px-3 py-2 text-center">% saisie</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {[...detection.examens_non_faits, ...detection.examens_partiels].slice(0, MAX_ROWS_DISPLAY).map((m, i) => (
                  <tr key={`${m.matiere_id}-${m.classe}-${i}`} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{m.matiere_nom}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">{m.matiere_code}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{m.classe}</td>
                    <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-300">{m.nb_etudiants}</td>
                    <td className="px-3 py-2 text-center text-green-600 dark:text-green-400">{m.nb_avec_note}</td>
                    <td className="px-3 py-2 text-center text-orange-500">{m.nb_absents}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-mono ${m.pct_saisie < 50 ? 'text-red-500' : m.pct_saisie < 80 ? 'text-orange-500' : 'text-green-600'}`}>
                        {m.pct_saisie}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        m.statut === 'non_fait'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}>
                        {m.statut === 'non_fait' ? 'Non fait' : 'Partiel'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleOuvrirZero(m)}
                        className="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs hover:bg-orange-200"
                        title="Attribuer 0 aux absents / manquants"
                      >
                        <UserX size={12} /> Zéro absents
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(detection.examens_non_faits.length + detection.examens_partiels.length) > MAX_ROWS_DISPLAY && (
              <div className="p-3 text-xs text-center text-gray-500 bg-gray-50 dark:bg-gray-900 border-t">
                Affichage limité à {MAX_ROWS_DISPLAY} lignes sur {detection.examens_non_faits.length + detection.examens_partiels.length}.
                Affinez les filtres pour voir les autres.
              </div>
            )}
          </div>
        </div>
      ) : detection ? (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="text-green-600" size={24} />
          <div className="text-sm text-green-700 dark:text-green-300">
            Aucune anomalie détectée pour {semestre}
            {filtreNiveau ? ` · ${filtreNiveau}` : ''}
            {filtrePole ? ` · ${filtrePole}` : ''}.
          </div>
        </div>
      ) : null}

      {/* Historique des batches */}
      {batchesData && batchesData.batches.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 text-sm font-medium">
            <History size={14} /> Historique des actions récentes
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {batchesData.batches.map(b => (
              <div key={b.batch_id} className="px-4 py-3 flex items-center gap-3 text-sm">
                <div className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                  b.annule
                    ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                }`}>
                  {labelType(b.type_operation)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 dark:text-gray-100 truncate">
                    {b.matiere_nom || '—'}{b.auteur ? ` · par ${b.auteur}` : ''}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {b.nb_modifications} modif. · {new Date(b.date).toLocaleString('fr-FR')}
                  </div>
                </div>
                {b.annule ? (
                  <span className="text-xs text-gray-400">Annulé</span>
                ) : (
                  <button
                    onClick={() => handleAnnuler(b.batch_id)}
                    disabled={annulatingBatch}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded hover:bg-red-100 border border-red-200 dark:border-red-800"
                  >
                    <Undo2 size={12} /> Annuler
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal : Attribuer zéro aux absents */}
      {zeroCtx && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <UserX size={18} className="text-orange-600" />
                  Attribuer 0 aux absents / manquants
                </h3>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {zeroCtx.matiereNom} · Classe {zeroCtx.classe}
                </div>
              </div>
              <button onClick={() => setZeroCtx(null)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {!zeroAnalyse ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">Analyse en cours…</div>
            ) : (
              <>
                {/* Stats */}
                <div className="p-4 grid grid-cols-4 gap-2 text-center text-xs border-b border-gray-200 dark:border-gray-700">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-2">
                    <div className="text-gray-500">Total</div>
                    <div className="font-bold">{zeroAnalyse.stats.total}</div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                    <div className="text-green-600">Avec note</div>
                    <div className="font-bold text-green-700 dark:text-green-400">{zeroAnalyse.stats.pct_avec_note}%</div>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded p-2">
                    <div className="text-orange-600">Absents</div>
                    <div className="font-bold text-orange-700 dark:text-orange-400">{zeroAnalyse.stats.pct_absents}%</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                    <div className="text-red-600">Manquants</div>
                    <div className="font-bold text-red-700 dark:text-red-400">{zeroAnalyse.stats.pct_manquants}%</div>
                  </div>
                </div>

                {/* Liste étudiants */}
                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                  {zeroAnalyse.absents.length === 0 && zeroAnalyse.manquants.length === 0 && (
                    <div className="text-center text-sm text-green-600 py-4">
                      ✓ Tous les étudiants ont leur note.
                    </div>
                  )}
                  {zeroAnalyse.absents.length > 0 && (
                    <>
                      <div className="text-xs font-medium text-orange-600 dark:text-orange-400 mt-2">Absents ({zeroAnalyse.absents.length})</div>
                      {zeroAnalyse.absents.map(e => (
                        <label key={e.etudiant_id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-sm">
                          <input
                            type="checkbox"
                            checked={zeroSelection.has(e.etudiant_id)}
                            onChange={() => toggleSelectionZero(e.etudiant_id)}
                          />
                          <span className="flex-1">
                            <span className="font-mono text-xs text-gray-500">{e.matricule}</span>
                            {' · '}
                            <span>{e.nom} {e.prenoms}</span>
                          </span>
                          <span className="text-[10px] text-orange-500">absent</span>
                        </label>
                      ))}
                    </>
                  )}
                  {zeroAnalyse.manquants.length > 0 && (
                    <>
                      <div className="text-xs font-medium text-red-600 dark:text-red-400 mt-3">Manquants (aucune note saisie) ({zeroAnalyse.manquants.length})</div>
                      {zeroAnalyse.manquants.map(e => (
                        <label key={e.etudiant_id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-sm">
                          <input
                            type="checkbox"
                            checked={zeroSelection.has(e.etudiant_id)}
                            onChange={() => toggleSelectionZero(e.etudiant_id)}
                          />
                          <span className="flex-1">
                            <span className="font-mono text-xs text-gray-500">{e.matricule}</span>
                            {' · '}
                            <span>{e.nom} {e.prenoms}</span>
                          </span>
                          <span className="text-[10px] text-red-500">manquant</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>

                {/* Motif + commentaire + actions */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select value={zeroForm.motif} onChange={e => setZeroForm(f => ({ ...f, motif: e.target.value }))}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700">
                      {MOTIFS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <input
                      value={zeroForm.commentaire}
                      onChange={e => setZeroForm(f => ({ ...f, commentaire: e.target.value }))}
                      placeholder="Commentaire (facultatif)"
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 flex-1">
                      {zeroSelection.size} sélectionné(s) recevront note=0, absent=true
                    </span>
                    <button
                      onClick={() => setZeroCtx(null)}
                      className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleAppliquerZero}
                      disabled={applyingZero || zeroSelection.size === 0}
                      className="px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded flex items-center gap-1"
                    >
                      {applyingZero ? <RefreshCw size={12} className="animate-spin" /> : <ClipboardCheck size={12} />}
                      Appliquer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal : Note collective */}
      {showColl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Copy size={18} className="text-indigo-600" /> Note collective (examen)
              </h3>
              <button onClick={() => setShowColl(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400">Matière</label>
                <input
                  value={matiereSearch}
                  onChange={e => { setMatiereSearch(e.target.value); setCollForm(f => ({ ...f, matiere_id: '' })); }}
                  placeholder="Rechercher (nom ou code)..."
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                />
                {matiereSearch.length >= 2 && !collForm.matiere_id && (
                  <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded max-h-40 overflow-y-auto">
                    {matieresFiltrees.map((m: any) => (
                      <button key={m.id} type="button"
                        onClick={() => { setCollForm(f => ({ ...f, matiere_id: String(m.id) })); setMatiereSearch(`${m.code_matiere} · ${m.nom_matiere}`); }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
                      >
                        <span className="font-mono text-gray-500">{m.code_matiere}</span> · {m.nom_matiere}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400">Portée de l'attribution</label>
                <select
                  value={collForm.scope}
                  onChange={e => { setCollForm(f => ({ ...f, scope: e.target.value as any })); setCollPreview(null); }}
                  className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                >
                  <option value="classes">Classes sélectionnées</option>
                  <option value="niveau">Niveau entier (tous étudiants du niveau suivant la matière)</option>
                  <option value="parcours">Parcours complet (ex : Génie Mécanique)</option>
                  <option value="matiere">Matière complète (toutes les classes concernées)</option>
                </select>
              </div>

              {collForm.scope === 'classes' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Classes (sélection multiple)</label>
                  <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                    {(classes as any[]).map((c: any) => (
                      <button key={c.id} type="button" onClick={() => { toggleClasseColl(c.nom); setCollPreview(null); }}
                        className={`px-2 py-1 text-xs rounded ${
                          collForm.classes.includes(c.nom)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {c.nom}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {collForm.scope === 'niveau' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Niveau</label>
                  <select
                    value={collForm.scopeNiveau}
                    onChange={e => { setCollForm(f => ({ ...f, scopeNiveau: e.target.value })); setCollPreview(null); }}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  >
                    <option value="">— Choisir un niveau —</option>
                    {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}

              {collForm.scope === 'parcours' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Parcours</label>
                  <select
                    value={collForm.scopeParcours}
                    onChange={e => { setCollForm(f => ({ ...f, scopeParcours: e.target.value })); setCollPreview(null); }}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  >
                    <option value="">— Choisir un parcours —</option>
                    {(parcoursList as any[]).map((p: any) => (
                      <option key={p.id} value={p.nom}>{p.pole_nom} / {p.nom}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Bouton preview + affichage résultat */}
              <div className="flex items-center gap-2">
                <button type="button" onClick={handlePreviewScope}
                  disabled={!collForm.matiere_id}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                  Calculer le périmètre
                </button>
                {collPreview && (
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    <strong>{collPreview.nb_classes}</strong> classe(s) · <strong>{collPreview.nb_etudiants}</strong> étudiant(s)
                    {collPreview.classes.length > 0 && ` : ${collPreview.classes.slice(0, 5).join(', ')}${collPreview.classes.length > 5 ? ` +${collPreview.classes.length - 5}` : ''}`}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Note à attribuer (0-20)</label>
                  <input
                    type="number" step="0.25" min="0" max="20"
                    value={collForm.valeur}
                    onChange={e => setCollForm(f => ({ ...f, valeur: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 mt-5">
                  <input type="checkbox" checked={collForm.ecraser}
                    onChange={e => setCollForm(f => ({ ...f, ecraser: e.target.checked }))} />
                  Écraser notes existantes
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <select value={collForm.motif} onChange={e => setCollForm(f => ({ ...f, motif: e.target.value }))}
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700">
                  {MOTIFS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <input
                  value={collForm.commentaire}
                  onChange={e => setCollForm(f => ({ ...f, commentaire: e.target.value }))}
                  placeholder="Commentaire (facultatif)"
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button onClick={() => setShowColl(false)}
                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200">
                Annuler
              </button>
              <button onClick={handleAppliquerCollective} disabled={applyingColl}
                className="px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded flex items-center gap-1">
                {applyingColl ? <RefreshCw size={12} className="animate-spin" /> : <Copy size={12} />}
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal : Note identique aux étudiants ayant une note <= seuil */}
      {showSeuil && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Filter size={18} className="text-teal-600" /> Note identique aux notes ≤ seuil
              </h3>
              <button onClick={() => setShowSeuil(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Attribue une note identique à tous les étudiants dont la note d'examen
                actuelle est <strong>inférieure ou égale</strong> au seuil. Les notes au‑dessus du seuil ne sont pas touchées.
              </p>

              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400">Matière</label>
                <input
                  value={seuilMatiereSearch}
                  onChange={e => { setSeuilMatiereSearch(e.target.value); setSeuilForm(f => ({ ...f, matiere_id: '' })); setSeuilPreview(null); }}
                  placeholder="Rechercher (nom ou code)..."
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                />
                {seuilMatiereSearch.length >= 2 && !seuilForm.matiere_id && (
                  <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded max-h-40 overflow-y-auto">
                    {seuilMatieresFiltrees.map((m: any) => (
                      <button key={m.id} type="button"
                        onClick={() => { setSeuilForm(f => ({ ...f, matiere_id: String(m.id) })); setSeuilMatiereSearch(`${m.code_matiere} · ${m.nom_matiere}`); }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
                      >
                        <span className="font-mono text-gray-500">{m.code_matiere}</span> · {m.nom_matiere}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-600 dark:text-gray-400">Portée de l'attribution</label>
                <select
                  value={seuilForm.scope}
                  onChange={e => { setSeuilForm(f => ({ ...f, scope: e.target.value as any })); setSeuilPreview(null); }}
                  className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                >
                  <option value="classes">Classes sélectionnées</option>
                  <option value="niveau">Niveau entier (tous étudiants du niveau suivant la matière)</option>
                  <option value="parcours">Parcours complet (ex : Génie Mécanique)</option>
                  <option value="matiere">Matière complète (toutes les classes concernées)</option>
                </select>
              </div>

              {seuilForm.scope === 'classes' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Classes (sélection multiple)</label>
                  <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                    {(classes as any[]).map((c: any) => (
                      <button key={c.id} type="button" onClick={() => { toggleClasseSeuil(c.nom); setSeuilPreview(null); }}
                        className={`px-2 py-1 text-xs rounded ${
                          seuilForm.classes.includes(c.nom)
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {c.nom}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {seuilForm.scope === 'niveau' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Niveau</label>
                  <select
                    value={seuilForm.scopeNiveau}
                    onChange={e => { setSeuilForm(f => ({ ...f, scopeNiveau: e.target.value })); setSeuilPreview(null); }}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  >
                    <option value="">— Choisir un niveau —</option>
                    {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}

              {seuilForm.scope === 'parcours' && (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Parcours</label>
                  <select
                    value={seuilForm.scopeParcours}
                    onChange={e => { setSeuilForm(f => ({ ...f, scopeParcours: e.target.value })); setSeuilPreview(null); }}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  >
                    <option value="">— Choisir un parcours —</option>
                    {(parcoursList as any[]).map((p: any) => (
                      <option key={p.id} value={p.nom}>{p.pole_nom} / {p.nom}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button type="button" onClick={handlePreviewSeuil}
                  disabled={!seuilForm.matiere_id}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                  Calculer le périmètre
                </button>
                {seuilPreview && (
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    <strong>{seuilPreview.nb_classes}</strong> classe(s) · <strong>{seuilPreview.nb_etudiants}</strong> étudiant(s) au total
                    <span className="opacity-70"> (seuls ceux ≤ seuil seront modifiés)</span>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Seuil — notes ≤ (0-20)</label>
                  <input
                    type="number" step="0.25" min="0" max="20"
                    value={seuilForm.seuil}
                    onChange={e => setSeuilForm(f => ({ ...f, seuil: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Note à attribuer (0-20)</label>
                  <input
                    type="number" step="0.25" min="0" max="20"
                    value={seuilForm.valeur}
                    onChange={e => setSeuilForm(f => ({ ...f, valeur: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={seuilForm.inclureAbsents}
                  onChange={e => setSeuilForm(f => ({ ...f, inclureAbsents: e.target.checked }))} />
                Inclure les étudiants marqués absents (leur statut absent sera retiré)
              </label>

              <div className="grid grid-cols-2 gap-3">
                <select value={seuilForm.motif} onChange={e => setSeuilForm(f => ({ ...f, motif: e.target.value }))}
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700">
                  {MOTIFS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <input
                  value={seuilForm.commentaire}
                  onChange={e => setSeuilForm(f => ({ ...f, commentaire: e.target.value }))}
                  placeholder="Commentaire (facultatif)"
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button onClick={() => setShowSeuil(false)}
                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200">
                Annuler
              </button>
              <button onClick={handleAppliquerSeuil} disabled={applyingSeuil}
                className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded flex items-center gap-1">
                {applyingSeuil ? <RefreshCw size={12} className="animate-spin" /> : <Filter size={12} />}
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionsRapidesExamens;
